import { FC, MouseEvent, ReactNode, useMemo } from 'react';
import { LoadingSpinner } from 'shared/components/LoadingSpinner/LoadingSpinner';
import { mergeClasses } from 'shared/utils/mergeClasses';

type Size = 'lg' | 'md' | 'sm';
export type Variant = 'contained' | 'outline' | 'text';
export type Color = 'primary' | 'secondary' | 'black' | 'danger' | 'gray' | 'white';

const getVariantClassNames = (variant: Variant, color: Color) => {
  switch (variant) {
    case 'contained':
      switch (color) {
        case 'primary':
          return "overflow-hidden border rounded-lg bg-primary-500 active:enabled:bg-primary-700 border-primary hover:enabled:border-primary-600 active:enabled:border-primary-700 text-white [&>*]:text-white [&_.spinner]:!border-r-white [&_.spinner]:!border-t-white after:absolute after:block after:h-full after:w-full after:content-[''] after:bg-button-gradient after:-right-full after:transition-all enabled:hover:after:right-0 after:top-0";
        case 'secondary':
          return 'border rounded-lg bg-secondary-500 hover:enabled:bg-secondary-600 active:enabled:bg-secondary-700 border-secondary hover:enabled:border-secondary-600 active:enabled:border-secondary-700 text-black [&>*]:text-black [&_.spinner]:!border-r-black [&_.spinner]:!border-t-black';
        case 'black':
          return 'border rounded-lg bg-black-500 hover:enabled:bg-opacity-90 active:enabled:bg-opacity-80 border-black hover:enabled:border-black-600 active:enabled:border-black-700 text-white [&>*]:text-white [&_.spinner]:!border-r-white [&_.spinner]:!border-t-white';
        case 'danger':
          return 'border rounded-lg bg-red-500 hover:enabled:bg-red-600 active:enabled:bg-red-700 border-red hover:enabled:border-red-600 active:enabled:border-red-700 text-white [&>*]:text-white [&_.spinner]:!border-r-white [&_.spinner]:!border-t-white';
        case 'white':
          return 'border rounded-lg bg-white hover:enabled:bg-gray-100 active:enabled:bg-gray-200 border-white hover:enabled:border-gray-100 active:enabled:border-gray-200 text-black [&>*]:text-black [&_.spinner]:!border-r-black [&_.spinner]:!border-t-black';
        default:
          return '';
      }
    case 'outline':
      switch (color) {
        case 'primary':
          return 'border rounded-lg border-primary-500 hover:enabled:border-primary-600 active:enabled:border-primary-700 hover:enabled:border-primary-600 hover:enabled:text-primary-600 active:enabled:text-primary-700 hover:enabled:bg-primary-600/5 active:enabled:bg-primary-700/10 active:enabled:border-primary-700 text-primary [&>*]:text-primary [&_.spinner]:!border-r-primary [&_.spinner]:!border-t-primary';
        case 'secondary':
          return 'border rounded-lg border-secondary-500 hover:enabled:border-secondary-600 active:enabled:border-secondary-700 hover:enabled:border-secondary-600 hover:enabled:text-secondary-600 active:enabled:text-secondary-700 hover:enabled:bg-secondary-600/5 active:enabled:bg-secondary-700/10 active:enabled:border-secondary-700 text-secondary [&>*]:text-secondary [&_.spinner]:!border-r-secondary [&_.spinner]:!border-t-secondary';
        case 'black':
          return 'border rounded-lg border-black-500 active:enabled:border-black-700 hover:enabled:border-black/90 hover:enabled:text-black/90 active:enabled:text-black/80 hover:enabled:bg-gray-300/20 active:enabled:bg-black/10 active:enabled:border-black-700 text-black [&>*]:text-black [&_.spinner]:!border-r-black [&_.spinner]:!border-t-black';
        case 'danger':
          return 'border rounded-lg border-red-500 hover:enabled:border-red-600 active:enabled:border-red-700 hover:enabled:border-red-600 hover:enabled:text-red-600 active:enabled:text-red-700 hover:enabled:bg-red-600/5 active:enabled:bg-red-700/10 active:enabled:border-red-700 text-red [&>*]:text-red [&_.spinner]:!border-r-red [&_.spinner]:!border-t-red';
        case 'gray':
          return 'border rounded-lg border-gray-200 hover:enabled:bg-gray-300/20 text-primary hover:enabled:text-primary-600 active:enabled:text-primary-700 active:enabled:bg-gray-700/10 text-gray [&>*]:text-primary [&_.spinner]:!border-r-gray [&_.spinner]:!border-t-gray bg-white focus-visible:!outline focus-visible:outline-gray-700';
        case 'white':
          return 'border rounded-lg border-white text-white hover:enabled:border-gray-200 hover:enabled:text-gray-200 active:enabled:border-gray-300 active:enabled:text-gray-300 hover:enabled:bg-white/10 active:enabled:bg-white/20 [&>*]:text-white [&_.spinner]:!border-r-white [&_.spinner]:!border-t-white';
        default:
          return '';
      }
    case 'text':
      switch (color) {
        case 'primary':
          return 'text-primary hover:enabled:text-primary-600 active:enabled:text-primary-700 [&_.spinner]:!border-r-primary [&_.spinner]:!border-t-primary';
        case 'secondary':
          return 'text-secondary hover:enabled:text-secondary-600 active:enabled:text-secondary-700 [&_.spinner]:!border-r-secondary [&_.spinner]:!border-t-secondary';
        case 'black':
          return 'text-black hover:enabled:text-black/70 active:enabled:text-black/50 [&_.spinner]:!border-r-black [&_.spinner]:!border-t-black';
        case 'danger':
          return 'text-red hover:enabled:text-red-600 active:enabled:text-red-700 [&_.spinner]:!border-r-red [&_.spinner]:!border-t-red';
        case 'gray':
          return 'text-gray hover:enabled:text-gray-400 active:enabled:text-gray-600 [&_.spinner]:!border-r-gray [&_.spinner]:!border-t-gray';
        case 'white':
          return 'text-white hover:enabled:text-white/80 active:enabled:text-white/50 [&_.spinner]:!border-r-white [&_.spinner]:!border-t-white';
        default:
          return '';
      }
    default:
      return '';
  }
};

const getPaddingClassNames = (size: Size) => {
  switch (size) {
    case 'sm':
      return 'py-1.5 px-4';
    case 'md':
      return 'py-[12.5px] px-6';
    case 'lg':
      return 'py-3.5 px-4 lg:px-6 text-lg';
    default:
      return '';
  }
};

const getFontSizeClassNames = (size: Size) => {
  switch (size) {
    case 'sm':
      return 'text-xs';
    case 'md':
      return 'text-sm';
    case 'lg':
      return 'text-lg';
    default:
      return '';
  }
};

export interface ButtonProps {
  name: string;
  children: ReactNode;
  className?: string;
  size?: Size;
  variant?: Variant;
  color?: Color;
  type?: HTMLButtonElement['type'];
  ariaLabel?: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  loading?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
}

export const Button: FC<ButtonProps> = ({
  className = '',
  size = 'md',
  variant = 'contained',
  color = 'primary',
  type = 'button',
  onClick,
  name,
  loading = false,
  disabled = false,
  fullWidth = false,
  ariaLabel,
  children,
}) => {
  const variantClassName = getVariantClassNames(variant, color);
  const sizeClassName = variant === 'text' ? '' : getPaddingClassNames(size);
  const fontSizeClassName = getFontSizeClassNames(size);

  const content = useMemo(() => {
    if (variant === 'contained' && color === 'primary') {
      return (
        <span className="relative z-10 flex items-center justify-center">
          {loading ? <LoadingSpinner size={size === 'sm' ? 14 : 21} /> : children}
        </span>
      );
    }
    return loading ? <LoadingSpinner size={size === 'sm' ? 14 : 21} /> : children;
  }, [variant, color, size, children, loading]);

  return (
    <button
      disabled={disabled}
      data-test-id={name}
      aria-label={ariaLabel}
      className={mergeClasses(
        className,
        variantClassName,
        sizeClassName,
        fontSizeClassName,
        'flex items-center justify-center font-medium transition-all disabled:cursor-not-allowed disabled:opacity-50',
        variant === 'contained' && color === 'primary' ? 'relative' : '',
        fullWidth ? 'w-full' : '',
      )}
      type={type}
      id={name}
      name={name}
      onClick={onClick}
    >
      {content}
    </button>
  );
};
